import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg, fmtDate, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge, { SeverityBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, ShieldAlert, RefreshCw, Lock, Unlock, Radar, Mail, CheckCircle2, XCircle } from "lucide-react";

const SCORE_COLOR = (s) => (s >= 80 ? "#DC2626" : s >= 60 ? "#F47920" : "#12346B");

export function RiskRadar() {
  const [data, setData] = useState(null);
  const load = useCallback(() => { setData(null); api.get("/risk/scan").then(({ data }) => setData(data)).catch(() => setData({ alerts: [], count: 0 })); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Radar className="h-6 w-6 text-[#F47920]" /> AI Risk Radar</h1>
          <p className="text-sm text-slate-500 mt-0.5">Explainable anomaly detection · duplicate payments, price anomalies, repeated variances</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="risk-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Rescan</Button>
      </div>
      {!data ? <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        : data.alerts.length === 0 ? <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-slate-400 text-sm">No anomalies detected in current data.</div>
        : (
          <div className="space-y-3">
            {data.alerts.map((a) => (
              <div key={a.id} data-testid={`risk-alert-${a.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex gap-4">
                <div className="flex flex-col items-center justify-center w-16 shrink-0">
                  <div className="relative h-14 w-14 rounded-full flex items-center justify-center" style={{ background: `conic-gradient(${SCORE_COLOR(a.score)} ${a.score * 3.6}deg, #E2E8F0 0deg)` }}>
                    <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center font-head font-bold text-sm" style={{ color: SCORE_COLOR(a.score) }}>{a.score}</div>
                  </div>
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 mt-1">risk</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ShieldAlert className="h-4 w-4 text-[#F47920]" />
                    <span className="font-head font-semibold text-slate-900">{a.category}</span>
                    <SeverityBadge level={a.severity} />
                    <span className="text-sm text-slate-500">· {a.entity}</span>
                  </div>
                  <ul className="mt-2 space-y-0.5">
                    {a.factors.map((f, i) => <li key={`${a.id}-${i}`} className="text-sm text-slate-600 flex gap-1.5"><span className="text-[#F47920]">•</span>{f}</li>)}
                  </ul>
                  <div className="mt-2 text-xs text-slate-500"><b>References:</b> <span className="mono">{a.references.join(", ")}</span></div>
                  <div className="mt-1.5 rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-700"><b className="text-[#12346B]">Recommended action:</b> {a.recommendation}</div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

export function Periods() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({});
  const load = useCallback(() => api.get("/periods").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const canManage = ["admin", "finance", "executive"].includes(user?.role);
  const canReopen = ["admin", "executive"].includes(user?.role);

  const create = async () => {
    if (!f.name || !f.start || !f.end) { toast.error("Name, start and end required"); return; }
    try { await api.post("/periods", f); toast.success("Period created"); setF({}); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (id, action) => {
    try { await api.post(`/periods/${id}/${action}`); toast.success(`Period ${action}ed`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900">Fiscal Period Control</h1>
        <p className="text-sm text-slate-500 mt-0.5">Lock closed periods so financial postings cannot be backdated without authorized reopening.</p>
      </div>
      {canManage && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Period Name</Label><Input className="w-40" placeholder="2026-06" value={f.name || ""} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="period-name" /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Start</Label><Input type="date" value={f.start || ""} onChange={(e) => setF((s) => ({ ...s, start: e.target.value }))} data-testid="period-start" /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">End</Label><Input type="date" value={f.end || ""} onChange={(e) => setF((s) => ({ ...s, end: e.target.value }))} data-testid="period-end" /></div>
          <Button onClick={create} className="bg-[#12346B] hover:bg-[#12346B]/90" data-testid="period-create">Create Period</Button>
        </div>
      )}
      {!rows ? <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div> : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <Table className="pf-table">
            <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Period", "Start", "End", "Status", "Locked By", "Actions"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-400 text-sm">No fiscal periods defined.</TableCell></TableRow>
                : rows.map((p) => (
                  <TableRow key={p.id} className="border-slate-100" data-testid={`period-${p.id}`}>
                    <TableCell className="py-2 px-3 font-semibold mono text-sm">{p.name}</TableCell>
                    <TableCell className="py-2 px-3 text-xs text-slate-500">{fmtDay(p.start)}</TableCell>
                    <TableCell className="py-2 px-3 text-xs text-slate-500">{fmtDay(p.end)}</TableCell>
                    <TableCell className="py-2 px-3"><StatusBadge status={p.status} /></TableCell>
                    <TableCell className="py-2 px-3 text-xs text-slate-500">{p.locked_by || "—"}</TableCell>
                    <TableCell className="py-2 px-3">
                      {p.status === "Open" ? (canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(p.id, "lock")} data-testid={`period-lock-${p.id}`}><Lock className="h-3.5 w-3.5 mr-1" /> Lock</Button>)
                        : (canReopen && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(p.id, "unlock")} data-testid={`period-unlock-${p.id}`}><Unlock className="h-3.5 w-3.5 mr-1" /> Reopen</Button>)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function Notifications() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/notifications").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const ico = { sent: <CheckCircle2 className="h-4 w-4 text-emerald-500" />, failed: <XCircle className="h-4 w-4 text-rose-500" />, skipped: <Mail className="h-4 w-4 text-slate-400" />, pending: <Mail className="h-4 w-4 text-orange-500" /> };
  return (
    <div>
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Mail className="h-6 w-6 text-[#12346B]" /> Notifications</h1>
        <p className="text-sm text-slate-500 mt-0.5">Automated status-change emails sent to suppliers and customers.</p>
      </div>
      {rows.length === 0 ? <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-slate-400 text-sm">No notifications sent yet. They are generated when payments, invoices, quotations or deliveries change status.</div>
        : (
          <div className="space-y-2">
            {rows.map((n) => (
              <div key={n.id} data-testid={`notif-${n.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                <div className="mt-0.5">{ico[n.email_status] || ico.pending}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slate-900 text-sm">{n.heading}</span>
                    <span className="text-[10px] uppercase tracking-wide bg-slate-100 rounded px-1.5 py-0.5 text-slate-500">{n.kind}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">To {n.to_name} &lt;{n.to_email || "no email"}&gt; · email {n.email_status}</div>
                  {(n.lines || []).map((l, i) => <div key={`${n.id}-${i}`} className="text-sm text-slate-600 mt-0.5">{l}</div>)}
                  <div className="text-[11px] text-slate-400 mt-1">{fmtDate(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
